import React, { useState, useRef, useEffect } from "react";
import {
  FormLabel,
  Input,
  VStack,
  Text,
  Progress,
  Box,
  useToast,
} from "@chakra-ui/react";

// Helper to upload file to a presigned URL
const uploadToPresignedUrl = async (file, presignedUrl) => {
  const response = await fetch(presignedUrl, {
    method: "PUT",
    headers: {
      "Content-Type": file.type,
    },
    body: file,
  });

  if (!response.ok) {
    throw new Error(`Upload failed with status ${response.status}`);
  }

  // The backend returns the s3Key, so we use that instead of parsing from the URL
  return true;
};

function FileUpload({
  fieldName,
  onFilesUpload,
  allowMultiple = false,
  accept,
  submissionId,
}) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedKeys, setUploadedKeys] = useState([]);
  const toast = useToast();
  const fileInputRef = useRef(null);

  useEffect(() => {
    setUploadedKeys([]);
    setFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [accept, fieldName]);

  const handleFileChange = (event) => {
    const selectedFiles = Array.from(event.target.files);
    const limitedFiles = allowMultiple
      ? selectedFiles
      : selectedFiles.slice(0, 1);
    setFiles((prevFiles) =>
      allowMultiple ? [...prevFiles, ...limitedFiles] : limitedFiles
    );
    setUploadedKeys([]);
    handleUpload(limitedFiles); // Use directly selected files
  };

const handleUpload = async (fileList = files) => {
  if (fileList.length === 0) {
    toast({
      title: 'No files selected.',
      status: 'warning',
      duration: 3000,
      isClosable: true,
    });
    return;
  }

  setUploading(true);
  setUploadProgress(0);
  const keys = [];

  try {
    for (let i = 0; i < fileList.length; i++) {
      const file = fileList[i];

      // Request presigned URL for this file
      const presignedUrlResponse = await fetch(`${process.env.REACT_APP_API_URL}/upload-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fileName: file.name,
          contentType: file.type,
          fieldName,
          submissionId,
        }),
      });

      if (!presignedUrlResponse.ok) {
        throw new Error(`Failed to get presigned URL for ${file.name}`);
      }

      const { uploadUrl, s3Key } = await presignedUrlResponse.json();

      // Upload the file to S3 using the presigned URL
      await uploadToPresignedUrl(file, uploadUrl);

      // Store the relative key (strip public/{submissionId}/)
      const prefix = `public/${submissionId}/`;
      const relativeKey = s3Key.startsWith(prefix) ? s3Key.slice(prefix.length) : s3Key;
      keys.push(relativeKey);

      const percent = Math.round(((i + 1) / fileList.length) * 100);
      setUploadProgress(percent);
    }

    setUploadedKeys(keys);
    onFilesUpload(allowMultiple ? keys : keys[0]);
    setUploading(false);
    setUploadProgress(0);
    toast({
      title: 'Upload successful.',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  } catch (error) {
    console.error('Error uploading file:', error);
    setUploading(false);
    setUploadProgress(0);
    toast({
      title: 'Upload failed.',
      description: error.message,
      status: 'error',
      duration: 5000,
      isClosable: true,
    });
  }
};

  const fileTypeText =
    accept === "video/*" ? "video" : accept === "image/*" ? "picture" : "file";
  const fileTypeTextPlural =
    accept === "video/*"
      ? "videos"
      : accept === "image/*"
      ? "pictures"
      : "files";
  const uploadOrRecordText =
    accept === "video/*"
      ? "Upload or Record Video"
      : accept === "image/*"
      ? "Upload or Take Picture"
      : "Upload File(s)";

  return (
    <VStack align="start" width="100%">
      <FormLabel
        htmlFor={`file-upload-${fieldName}`}
        mb={0}
        cursor="pointer"
        borderWidth="1px"
        borderRadius="md"
        p={3}
        width="100%"
        textAlign="center"
        _hover={{ borderColor: "teal.500" }}
        display="block"
      >
        {allowMultiple
          ? `Select ${fileTypeTextPlural}`
          : `Select a ${fileTypeText}`}{" "}
        or {uploadOrRecordText}
      </FormLabel>

      <Input
        id={`file-upload-${fieldName}`}
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        multiple={allowMultiple}
        accept={accept}
        position="absolute"
        left="-9999px"
        style={{ display: "none" }}
      />

      {files.length > 0 && (
        <Box>
          <Text fontSize="sm" color="gray.600">
            Selected:
          </Text>
          {files.map((file, index) => {
            const name = file.name;
            const truncated =
              name.length > 40
                ? name.slice(0, 18) + "..." + name.slice(-18)
                : name;
            return (
              <Text key={index} fontSize="sm" color="gray.500" ml={2}>
                • {truncated}
              </Text>
            );
          })}
        </Box>
      )}

      {uploading && (
        <Box width="100%">
          <Progress
            value={uploadProgress}
            size="sm"
            colorScheme="teal"
            hasStripe
            isAnimated
          />
          <Text fontSize="xs" textAlign="center" mt={1}>
            {uploadProgress}%
          </Text>
        </Box>
      )}
      {uploadedKeys.length > 0 && (
        <Box>
          <Text fontSize="sm" color="gray.600">
            Uploaded:
          </Text>
          {uploadedKeys.map((key, index) => {
            const fullName = key.split("/").pop();
            const truncatedName =
              fullName.length > 40
                ? fullName.slice(0, 18) + "..." + fullName.slice(-18)
                : fullName;
            return (
              <Text key={index} fontSize="sm" color="green.600" ml={2}>
                • {truncatedName}
              </Text>
            );
          })}
          <Text fontSize="sm" color="green.600" mt={1}>
            Uploaded: {uploadedKeys.length} file(s).
          </Text>
        </Box>
      )}
    </VStack>
  );
}

export default FileUpload;
